import { useState, useEffect } from 'react';
import { userApi } from '../../api/user.api';
import { parentChildApi } from '../../api/parent-child.api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  FormControlLabel,
  Switch,
  Box,
  Link,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import { OpenInNew, ArrowForward } from '@mui/icons-material';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  isActive: boolean;
  profile?: {
    btn?: string;
  };
}

interface RelatedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

interface Relationship {
  id: string;
  linkedAt: string;
  parent?: RelatedUser;
  child?: RelatedUser;
}

interface UserFormDialogProps {
  open: boolean;
  user: User | null;
  onClose: (refresh?: boolean) => void;
  onNavigateToUser?: (userId: string) => void;
}

export const UserFormDialog = ({ open, user, onClose, onNavigateToUser }: UserFormDialogProps) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'PLAYER',
    phone: '',
    dateOfBirth: '',
    gender: '',
    isActive: true,
  });
  const [btnValue, setBtnValue] = useState('');
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchRelationships = async (userId: string, role: string) => {
    try {
      if (role === 'PLAYER') {
        const response = await parentChildApi.getChildParents(userId);
        setRelationships(response.data.data || []);
      } else if (role === 'PARENT') {
        const response = await parentChildApi.getParentChildren(userId);
        setRelationships(response.data.data || []);
      } else {
        setRelationships([]);
      }
    } catch (err) {
      console.error('Failed to fetch relationships:', err);
      setRelationships([]);
    }
  };

  useEffect(() => {
    if (user) {
      // Convert ISO date to YYYY-MM-DD format for date input
      let formattedDate = '';
      if (user.dateOfBirth) {
        const date = new Date(user.dateOfBirth);
        formattedDate = date.toISOString().split('T')[0];
      }

      setFormData({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        phone: user.phone || '',
        dateOfBirth: formattedDate,
        gender: user.gender || '',
        isActive: user.isActive,
      });

      // Set BTN value from profile
      setBtnValue(user.profile?.btn || '');

      // Fetch relationships for players and parents
      if (user.role === 'PLAYER' || user.role === 'PARENT') {
        fetchRelationships(user.id, user.role);
      }
    } else {
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'PLAYER',
        phone: '',
        dateOfBirth: '',
        gender: '',
        isActive: true,
      });
      setBtnValue('');
      setRelationships([]);
    }
    setError('');
  }, [user, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (user) {
        // Update existing user
        const updateData: Record<string, unknown> = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || null,
          dateOfBirth: formData.dateOfBirth || null,
          gender: formData.gender || null,
          isActive: formData.isActive,
        };
        await userApi.updateUser(user.id, updateData);
      } else {
        // Create new user
        await userApi.createUser(formData);
      }
      onClose(true);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Operation failed');
      } else {
        setError('Operation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{user ? 'Edit User' : 'Create New User'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            {!user && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  helperText="Min 8 characters, must include uppercase, lowercase, number, and special character"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
                disabled={!!user}
              >
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="HEAD_COACH">Head Coach</MenuItem>
                <MenuItem value="COACH">Coach</MenuItem>
                <MenuItem value="PLAYER">Player</MenuItem>
                <MenuItem value="PARENT">Parent</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Gender"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                <MenuItem value="">Prefer not to say</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
                <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
              </TextField>
            </Grid>
            {user && formData.role === 'PLAYER' && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="British Tennis Number (BTN)"
                    value={btnValue}
                    disabled
                    helperText="Players manage their BTN from their profile page"
                  />
                  {btnValue && (
                    <Link
                      href={`https://competitions.lta.org.uk/find/player?q=${encodeURIComponent(btnValue)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
                    >
                      View on LTA <OpenInNew fontSize="small" />
                    </Link>
                  )}
                </Box>
              </Grid>
            )}
            {user && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            )}
          </Grid>

          {user && (formData.role === 'PLAYER' || formData.role === 'PARENT') && relationships.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {formData.role === 'PLAYER' ? 'Linked Parents' : 'Linked Players (Children)'}
              </Typography>
              <List dense>
                {relationships.map((rel) => {
                  const linkedUser = formData.role === 'PLAYER' ? rel.parent : rel.child;
                  if (!linkedUser) return null;
                  return (
                    <ListItem
                      key={rel.id}
                      disablePadding
                      secondaryAction={
                        onNavigateToUser && (
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => {
                              onNavigateToUser(linkedUser.id);
                              onClose();
                            }}
                          >
                            <ArrowForward />
                          </IconButton>
                        )
                      }
                    >
                      <ListItemButton
                        onClick={() => {
                          if (onNavigateToUser) {
                            onNavigateToUser(linkedUser.id);
                            onClose();
                          }
                        }}
                        sx={{ pr: 6 }}
                      >
                        <ListItemText
                          primary={`${linkedUser.firstName} ${linkedUser.lastName}`}
                          secondary={
                            <>
                              {linkedUser.email}
                              {linkedUser.phone && ` • ${linkedUser.phone}`}
                              <Typography component="span" variant="caption" display="block" color="text.secondary">
                                Linked: {new Date(rel.linkedAt).toLocaleDateString()}
                              </Typography>
                            </>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : user ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
